<app-dashboard-shell
  title="Self Improving AI"
  [lastReload]="lastReload"
  [loading]="loading"
  [showRefresh]="true"
  (refresh)="triggerReload()"
  class="self-improving-ai-container"
>
  <div class="p-col-12">
    <div class="dashboard-card">
      <div class="dashboard-card-header p-mb-3">
        <div>
          <h4 class="p-mb-0">Relevant Instruction Pages</h4>
          <p class="p-text-secondary p-mt-1">Expand to see referenced pages. Click a page name to navigate to it.</p>
        </div>
      </div>
      <div class="page-tree">
        <ng-container *ngFor="let node of pageTree">
          <ng-container *ngTemplateOutlet="pageTreeNode; context: { $implicit: node, depth: 0 }"></ng-container>
        </ng-container>
        <a [routerLink]="['/pages/self-improving-ai']" class="page-link">
          <i class="pi pi-android"></i>
          <span>Self Improving AI Goals</span>
        </a>
      </div>
    </div>
  </div>

  <ng-template #pageTreeNode let-node let-depth="depth">
    <div class="page-tree-node" [style.padding-left.px]="depth * 16">
      <button
        *ngIf="node.children?.length"
        class="page-tree-toggle"
        (click)="togglePath(node.path)"
        [attr.aria-expanded]="isPathExpanded(node.path)"
        [attr.aria-label]="'Toggle ' + node.title"
      >
        <i class="pi" [ngClass]="isPathExpanded(node.path) ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
      </button>
      <span *ngIf="!node.children?.length" class="page-tree-toggle page-tree-toggle--leaf">
        <i class="pi pi-file page-tree-leaf-icon"></i>
      </span>
      <a [routerLink]="[node.path]" class="page-tree-link">{{ node.title }}</a>
      <small *ngIf="node.children?.length" class="page-tree-count">{{ node.children.length }}</small>
    </div>
    <ng-container *ngIf="isPathExpanded(node.path) && node.children?.length">
      <ng-container *ngFor="let child of node.children">
        <ng-container *ngTemplateOutlet="pageTreeNode; context: { $implicit: child, depth: depth + 1 }"></ng-container>
      </ng-container>
    </ng-container>
  </ng-template>

  <div class="p-col-12">
    <div class="dashboard-card">
      <div class="dashboard-card-header p-mb-3">
        <div>
          <h4 class="p-mb-0"><i class="pi pi-search"></i> Focus Area</h4>
          <p class="p-text-secondary p-mt-1">Describe a specific issue to look for across all jobs (e.g. "customers autodialed when they shouldn't be" or "callers who abandon"). This guides both individual job analysis and the aggregated suggestion pass.</p>
        </div>
      </div>
      <input
        pInputText
        type="text"
        [(ngModel)]="theme"
        (ngModelChange)="onThemeChange()"
        placeholder="e.g. customers autodialed when they shouldn't be, callers who abandon..."
        class="theme-input"
      />
    </div>
  </div>

  <div class="p-col-12">
    <div class="dashboard-card">
      <div class="dashboard-card-header p-mb-3">
        <div>
          <h4 class="p-mb-0">Closed Jobs ({{ totalJobs }})</h4>
          <small *ngIf="hasMore" class="p-text-secondary">Showing first {{ jobs.length }} of {{ totalJobs }}</small>
        </div>
        <div class="p-d-flex p-ai-center p-gap-2">
          <button
            pButton
            pRipple
            type="button"
            label="Download CSV"
            icon="pi pi-download"
            class="p-button-sm p-button-outlined p-button-secondary"
            [disabled]="!jobs.length"
            (click)="downloadCSV()"
          ></button>
          <button
            pButton
            pRipple
            type="button"
            [label]="allAnalyzed() ? 'Re-analyze All' : 'Analyze All'"
            icon="pi pi-android"
            class="p-button-sm p-button-outlined"
            [loading]="runningAll"
            [disabled]="isAnalyzing() || !jobs.length"
            (click)="runAnalysisAll()"
          ></button>
        </div>
      </div>

      <p-blockUI [blocked]="loading">
        <i class="pi pi-spin pi-spinner" style="font-size: 3rem"></i>
      </p-blockUI>

      <app-blockable>
        <ng-container *ngIf="dayGroups.length > 0; else emptyState">
          <div *ngFor="let group of dayGroups" class="day-group">
             <div class="day-group__header" role="button" tabindex="0" (click)="toggleDayExpanded(group.dateKey)" (keydown.enter)="toggleDayExpanded(group.dateKey)" (keydown.space)="toggleDayExpanded(group.dateKey); $event.preventDefault()">
              <span class="day-group__label">
                <i class="pi" [ngClass]="isDayExpanded(group.dateKey) ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
                {{ group.label }} ({{ group.jobs.length }})
              </span>
              <span class="day-group__actions" (click)="$event.stopPropagation()">
                <button
                  pButton
                  pRipple
                  type="button"
                  [label]="isDayAnalyzed(group) ? 'Re-analyze Day' : 'Analyze Day'"
                  icon="pi pi-android"
                  class="p-button-sm p-button-outlined"
                  [loading]="isDayAnalyzing(group)"
                  [disabled]="isAnalyzing()"
                  (click)="runAnalysisDay(group)"
                ></button>
              </span>
            </div>
            <div *ngIf="isDayExpanded(group.dateKey)" class="day-group__content">
              <div *ngFor="let job of group.jobs" class="job-row">
                <div class="job-row__header">
                  <div class="job-row__info">
                    <a [href]="'/jobs/' + job.id + '/overview'" target="_blank" class="job-code-link">{{ job.code }}</a>
                    <span class="job-row__customer">{{ job.customerName }}</span>
                    <p-tag [value]="job.stage" severity="info" styleClass="p-ml-2"></p-tag>
                    <span *ngIf="job.closedReason" class="job-row__closed-reason">{{ job.closedReason }}</span>
                  </div>
                  <div class="job-row__meta">
                    <span class="job-row__time">{{ formatClosedTime(job.closedAt) }}</span>
                    <span *ngIf="job.closedBy" class="job-row__closed-by">{{ job.closedBy }}</span>
                    <span>{{ formatTotal(job.total, job.currency) }}</span>
                    <button
                      pButton
                      pRipple
                      type="button"
                      icon="pi pi-android"
                      [label]="job.analysis ? 'Re-analyze' : 'Analyze'"
                      class="p-button-sm p-button-outlined"
                      [loading]="job.analyzing"
                      [disabled]="isAnalyzing()"
                      (click)="runAnalysis(job.id)"
                    ></button>
                  </div>
                </div>

                <div *ngIf="job.analysis" class="analysis-inline">
                  <div class="analysis-inline__summary" markdown [data]="job.analysis.summary"></div>
                  <div class="analysis-inline__sections">
                    <div *ngIf="job.analysis.good.length" class="analysis-inline__section analysis-inline__section--good">
                      <strong><i class="pi pi-check-circle"></i> Good</strong>
                      <ul><li *ngFor="let item of job.analysis.good">{{ item }}</li></ul>
                    </div>
                    <div *ngIf="job.analysis.issues.length" class="analysis-inline__section analysis-inline__section--issue">
                      <strong><i class="pi pi-times-circle"></i> Issues</strong>
                      <ul><li *ngFor="let item of job.analysis.issues">{{ item }}</li></ul>
                    </div>
                  </div>
                </div>

                <div *ngIf="job.analyzing && !job.analysis" class="analysis-inline analysis-inline--loading">
                  <i class="pi pi-spin pi-spinner"></i> Analyzing...
                </div>
              </div>
            </div>
          </div>
        </ng-container>

        <ng-template #emptyState>
          <app-dashboard-empty-state
            *ngIf="!loading"
            text="No closed jobs in the selected period"
          ></app-dashboard-empty-state>
        </ng-template>
      </app-blockable>
    </div>
  </div>

  <div class="p-col-12" *ngIf="hasAnalyzedJobs() || hasAnyDiffs()">
    <div class="dashboard-card">
      <div class="dashboard-card-header p-mb-3">
        <div>
          <h4 class="p-mb-0"><i class="pi pi-android"></i> Suggest Page Improvements</h4>
          <p class="p-text-secondary p-mt-1">Uses all analyzed job feedback to propose edits to existing instruction pages. Prefers modifying existing procedures over creating new ones.</p>
        </div>
        <div class="p-d-flex p-ai-center p-gap-2">
          <button
            *ngIf="hasAnalyzedJobs()"
            pButton
            pRipple
            type="button"
            label="Download Analysis"
            icon="pi pi-download"
            class="p-button-sm p-button-outlined p-button-secondary"
            [disabled]="!hasAnalyzedJobs()"
            (click)="downloadAnalysisCSV()"
          ></button>
          <button
            pButton
            pRipple
            type="button"
            [label]="suggestingImprovements ? 'Generating...' : 'Suggest Improvements'"
            icon="pi pi-android"
            class="p-button-sm p-button-outlined"
            [loading]="suggestingImprovements"
            [disabled]="isAnalyzing() || !hasAnalyzedJobs()"
            (click)="suggestImprovements()"
          ></button>
        </div>
      </div>

      <div *ngIf="suggestingImprovements && !hasAnyDiffs() && !noChangesSuggested" class="suggest-loading">
        <i class="pi pi-spin pi-spinner"></i> Analyzing feedback from {{ analyzedCount() }} job(s) and generating improvement suggestions...
      </div>

      <div *ngIf="noChangesSuggested && !hasAnyDiffs()" class="no-changes-notice">
        <i class="pi pi-check-circle"></i> No page changes suggested. The current instructions already address the patterns found in the analyzed jobs.
      </div>

      <div *ngIf="memoryUpdated" class="memory-notice">
        <i class="pi pi-check-circle"></i> Self Improving AI memory page updated with this analysis.
      </div>

      <div *ngIf="changelog" class="changelog-section">
        <h5><i class="pi pi-history"></i> Changelog</h5>
        <div class="changelog-text" markdown [data]="changelog"></div>
      </div>

      <div *ngIf="overallSummary" class="summary-section">
        <h5><i class="pi pi-info-circle"></i> Overall Summary</h5>
        <div class="summary-text" markdown [data]="overallSummary"></div>
        <div *ngIf="themeFeedback" class="theme-feedback" markdown [data]="themeFeedback"></div>
      </div>

      <div *ngIf="hasAnyDiffs()">
        <div class="p-d-flex p-jc-end p-mb-2">
          <button
            pButton
            pRipple
            type="button"
            label="Apply All Changes"
            icon="pi pi-check"
            class="p-button-sm p-button-success"
            [disabled]="allPageDiffsApplied()"
            (click)="applyAllPageDiffs()"
          ></button>
        </div>

        <div *ngFor="let diff of summaryPageDiffs" class="page-diff">
          <div class="page-diff__header">
            <span class="page-diff__path">{{ diff.title }} <small>{{ diff.path }}</small></span>
            <span *ngIf="diff.isNewPage" class="page-diff__badge">New Page</span>
            <span *ngIf="diff.path.startsWith('/pages/bee')" class="page-diff__bee-badge">Bee Page</span>
            <button
              *ngIf="!diff.applied"
              pButton
              pRipple
              type="button"
              label="Apply"
              icon="pi pi-check"
              class="p-button-sm p-button-success"
              (click)="applyPageDiff(diff)"
            ></button>
            <span *ngIf="diff.applied" class="page-diff__applied">
              <i class="pi pi-check-circle"></i> Applied
            </span>
          </div>
          <div *ngIf="diff.changeSummary" class="page-diff__change-summary">{{ diff.changeSummary }}</div>
           <div class="page-diff__content">
             <div class="page-diff__diff">
               <div *ngFor="let line of diff.computedLines" [ngClass]="{
                 'diff-line--added': line.type === 'added',
                 'diff-line--removed': line.type === 'removed',
                 'diff-line--context': line.type === 'context'
               }" class="diff-line">
                 <span class="diff-line__prefix">{{ line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' ' }}</span>
                 <span class="diff-line__text">{{ line.content }}</span>
               </div>
             </div>
           </div>
        </div>
      </div>
    </div>
  </div>
</app-dashboard-shell>